import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Receipt, Edit2, Trash2, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export default function BillsPage() {
  const { user } = useAuth();
  const currency = user?.currency || 'GBP';
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [deletingBill, setDeletingBill] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    due_date: ''
  });

  const fetchBills = async () => {
    try {
      const response = await fetch(`${API}/bills`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBills(data);
      }
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.amount || !formData.due_date) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const url = editingBill 
        ? `${API}/bills/${editingBill.bill_id}`
        : `${API}/bills`;
      
      const response = await fetch(url, {
        method: editingBill ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          amount: parseFloat(formData.amount),
          due_date: parseInt(formData.due_date),
          is_recurring: true
        })
      });

      if (response.ok) {
        toast.success(editingBill ? 'Bill updated' : 'Bill added');
        fetchBills();
        closeModal();
      } else {
        throw new Error('Failed to save bill');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async () => {
    if (!deletingBill) return;

    try {
      const response = await fetch(`${API}/bills/${deletingBill.bill_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Bill deleted');
        fetchBills();
        setDeletingBill(null);
      } else {
        throw new Error('Failed to delete bill');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditModal = (bill) => {
    setFormData({
      name: bill.name,
      amount: bill.amount.toString(),
      due_date: bill.due_date.toString()
    });
    setEditingBill(bill);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBill(null);
    setFormData({ name: '', amount: '', due_date: '' });
  };

  const totalBills = bills.reduce((sum, b) => sum + b.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1224] page-container" data-testid="bills-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top">
        <h1 className="font-heading text-2xl font-bold text-white">Bills</h1>
        <p className="text-[#6B7280]">Manage your recurring bills</p>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6B7280] text-sm">Total Monthly Bills</p>
              <p className="font-heading text-3xl font-bold text-white" data-testid="total-bills-amount">
                {formatCurrency(totalBills, currency)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-[#63E6E2]/10 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-[#63E6E2]" />
            </div>
          </div>
        </motion.div>

        {/* Add Bill Button */}
        <Button
          onClick={() => setShowAddModal(true)}
          className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
          data-testid="add-bill-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add New Bill
        </Button>

        {/* Bills List */}
        <div className="space-y-3">
          {bills.length > 0 ? (
            bills.map((bill, index) => (
              <motion.div
                key={bill.bill_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
                data-testid={`bill-item-${bill.bill_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#252A4A] flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-[#9CA3AF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{bill.name}</p>
                      <p className="text-[#6B7280] text-sm">Due: {bill.due_date}th of each month</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold mr-2">{formatCurrency(bill.amount, currency)}</span>
                    <button
                      onClick={() => openEditModal(bill)}
                      className="p-2 text-[#6B7280] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      data-testid={`edit-bill-${bill.bill_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingBill(bill)}
                      className="p-2 text-[#6B7280] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      data-testid={`delete-bill-${bill.bill_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#1B1F3A] border border-[#2D3354] flex items-center justify-center">
                <Receipt className="w-8 h-8 text-[#6B7280]" />
              </div>
              <p className="text-white font-medium mb-1">No bills yet</p>
              <p className="text-[#6B7280] text-sm">Add your first bill to get started</p>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Add/Edit Bill Modal */}
      <Dialog open={showAddModal} onOpenChange={closeModal}>
        <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingBill ? 'Edit Bill' : 'Add New Bill'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Bill Name</Label>
              <Input
                placeholder="e.g., Netflix, Rent"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                data-testid="bill-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">£</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="pl-7 h-12 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                  data-testid="bill-amount-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Due Date (Day of Month)</Label>
              <Input
                type="number"
                min="1"
                max="31"
                placeholder="1-31"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="h-12 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                data-testid="bill-due-date-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 border-[#2D3354] text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
                data-testid="save-bill-btn"
              >
                {editingBill ? 'Update' : 'Add Bill'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingBill} onOpenChange={() => setDeletingBill(null)}>
        <AlertDialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Bill?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              Are you sure you want to delete "{deletingBill?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D3354] text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
